import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { useAuth } from '@/hooks/useAuth'
import { useState } from 'react'
import { SignupForm } from './SignupForm'
import { useLoginModal } from '@/contexts/LoginModalContext'
import { GoogleOAuthProvider, GoogleLogin, CredentialResponse } from '@react-oauth/google';
import { logError } from '@/lib/sentry'

// 카카오 로그인 버튼 이미지 import
import kakaoLoginButton from '@/assets/kakao_login.png'

interface LoginModalProps {
  open: boolean
  onClose: () => void
  onSignupComplete?: () => void
}

export function LoginModal({ open, onClose, onSignupComplete }: LoginModalProps) {
  const { signInWithGoogle, signInWithKakao } = useAuth()
  const { showSignupForm } = useLoginModal()
  const [isAuthLoading, setIsAuthLoading] = useState(false)
  const googleClientId = import.meta.env.VITE_GOOGLE_CLIENT_ID as string;

  const handleGoogleSignIn = async (response: CredentialResponse) => {
    setIsAuthLoading(true)
    try {
      await signInWithGoogle(response.credential ?? "")
      // 로그인 성공 후 모달을 닫지 않음 - AuthCallback에서 신규/기존 회원 판단 후 처리
      // (onClose()를 여기서 호출하면 App.tsx의 자동 리다이렉트와 경쟁 상태 발생)
    } catch {
      // 로그인 실패 시 모달 유지
    } finally {
      setIsAuthLoading(false)
    }
  }

  const handleGoogleSignInFailure = () => {
    logError("Google 로그인 실패");
    setIsAuthLoading(false)
  };

  const handleKakaoSignIn = async () => {
    setIsAuthLoading(true)
    try {
      await signInWithKakao()
    } finally {
      setIsAuthLoading(false)
    }
  }

  const handleSignupComplete = () => {
    console.log("[LoginModal] 회원가입 완료, onSignupComplete 콜백 호출");
    // onSignupComplete 콜백이 있으면 호출, 없으면 기본 onClose 실행
    if (onSignupComplete) {
      onSignupComplete();
    } else {
      onClose();
    }
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>
            {showSignupForm ? '회원가입' : '로그인'}
          </DialogTitle>
        </DialogHeader>
        {showSignupForm ? (
          <SignupForm onComplete={handleSignupComplete} />
        ) : (
          <div className="flex flex-col gap-4 py-4">
            <button
              onClick={handleKakaoSignIn}
              className="w-full h-11 flex justify-center disabled:opacity-50 bg-[#FEE500] rounded-[4px]"
              disabled={isAuthLoading}
            >
              {isAuthLoading ? (
                <div className="flex items-center gap-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-900"></div>
                  <span>처리중...</span>
                </div>
              ) : (
                <img 
                  src={kakaoLoginButton} 
                  alt="카카오 로그인" 
                  className="h-full object-contain"
                />
              )}
            </button>
            <GoogleOAuthProvider clientId={googleClientId}>
              {isAuthLoading ? (
                <div className="flex items-center justify-center h-10 w-full border border-gray-200 rounded-[4px]">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-900"></div>
                </div>
              ) : (
                <GoogleLogin
                  onSuccess={handleGoogleSignIn}
                  onError={handleGoogleSignInFailure}
                  logo_alignment='center'
                />
              )}
            </GoogleOAuthProvider>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
